import { FormEvent, useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import type { WhatsAppSession, Assistant, Integration } from '../../types/api';
import type { TimeCondition } from '../../types/timeCondition';
import { generateToken } from '../../lib/token';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormCheckbox, Button } from '../ui';

interface SessionFormProps {
  session: WhatsAppSession | null;
  assistants: Assistant[];
  integrations?: Integration[];
  timeConditions?: TimeCondition[];
  userId: number;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

type EngineType = 'GMAIL' | 'TICKETZ' | 'SMTP' | 'DIGISAC' | 'GOWS' | 'OPA' | 'INTERNAL' | 'TWILLIO' | 'FORTICS';

interface FormData {
  session_name: string;
  assistant_id: string;
  token: string;
  engine: EngineType;
  channel: string;
  voice_number: string;
  integration_id: string;
  messages_per_minute: string;
  messages_per_hour: string;
  messages_time_condition_id: string;
  send_alert_at: string;
  isDefault: boolean;
  smtp_host: string;
  smtp_port: string;
  smtp_username: string;
  smtp_password: string;
  smtp_secure: boolean;
}

function SessionForm({
  session,
  assistants,
  integrations,
  timeConditions,
  userId,
  onSubmit,
  isLoading,
  onClose,
}: SessionFormProps) {
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState<FormData>({
    session_name: session?.session_name || '',
    assistant_id: session?.assistant_id?.toString() || '',
    token: session?.token || '',
    engine: session?.engine || 'GOWS',
    channel: session?.channel || 'all',
    voice_number: session?.voice_number || '',
    integration_id: session?.integration_id?.toString() || '',
    messages_per_minute: session?.messages_per_minute?.toString() || '2',
    messages_per_hour: session?.messages_per_hour?.toString() || '20',
    messages_time_condition_id: session?.messages_time_condition_id?.toString() || '',
    send_alert_at: session?.send_alert_at ? new Date(session.send_alert_at).toISOString().slice(0, 16) : '',
    isDefault: session?.isDefault || false,
    smtp_host: session?.smtp_host || '',
    smtp_port: session?.smtp_port?.toString() || '587',
    smtp_username: session?.smtp_username || '',
    smtp_password: '',
    smtp_secure: session?.smtp_secure !== false,
  });

  useEffect(() => {
    if (session) {
      setFormData({
        session_name: session.session_name || '',
        assistant_id: session.assistant_id?.toString() || '',
        token: session.token || '',
        engine: session.engine || 'GOWS',
        channel: session.channel || 'all',
        voice_number: session.voice_number || '',
        integration_id: session.integration_id?.toString() || '',
        messages_per_minute: session.messages_per_minute?.toString() || '2',
        messages_per_hour: session.messages_per_hour?.toString() || '20',
        messages_time_condition_id: session.messages_time_condition_id?.toString() || '',
        send_alert_at: session.send_alert_at ? new Date(session.send_alert_at).toISOString().slice(0, 16) : '',
        isDefault: session.isDefault || false,
        smtp_host: session.smtp_host || '',
        smtp_port: session.smtp_port?.toString() || '587',
        smtp_username: session.smtp_username || '',
        smtp_password: '',
        smtp_secure: session.smtp_secure !== false,
      });
    }
    setActiveTab('general');
  }, [session]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleGenerateToken = () => {
    const newToken = generateToken();
    setFormData(prev => ({ ...prev, token: newToken }));
  };

  const showSmtpFields = formData.engine === 'SMTP' || formData.engine === 'GMAIL';
  const showVoiceField = formData.engine === 'TWILLIO';

  return (
    <Form onSubmit={onSubmit}>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Geral
          </button>
          {showSmtpFields && (
            <button
              type="button"
              onClick={() => setActiveTab('smtp')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'smtp'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Configurações SMTP
            </button>
          )}
        </nav>
      </div>

      <div className={activeTab === 'general' ? 'block' : 'hidden'}>
        <div className="space-y-4">
          {!session && (
            <FormField>
              <FormLabel htmlFor="session_name" required>
                Nome da Sessão
              </FormLabel>
              <FormInput
                id="session_name"
                name="session_name"
                value={formData.session_name}
                onChange={handleInputChange}
                required
              />
            </FormField>
          )}

          <FormField>
            <FormLabel htmlFor="assistant_id">Assistente (opcional)</FormLabel>
            <FormSelect
              id="assistant_id"
              name="assistant_id"
              value={formData.assistant_id}
              onChange={handleInputChange}
            >
              <option value="">Selecione um assistente (opcional)</option>
              {assistants?.map((assistant: Assistant) => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.name} ({assistant.type})
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="token">Token</FormLabel>
            <div className="flex gap-2">
              <FormInput
                id="token"
                name="token"
                value={formData.token}
                onChange={handleInputChange}
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleGenerateToken}
                title="Gerar token"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </FormField>

          <FormField>
            <FormLabel htmlFor="engine" required>
              Engine
            </FormLabel>
            <FormSelect
              id="engine"
              name="engine"
              value={formData.engine}
              onChange={handleInputChange}
              required
            >
              <option value="GOWS">GOWS (WhatsApp)</option>
              <option value="TICKETZ">TICKETZ</option>
              <option value="DIGISAC">DIGISAC</option>
              <option value="GMAIL">GMAIL</option>
              <option value="SMTP">SMTP</option>
              <option value="OPA">OPA</option>
              <option value="INTERNAL">INTERNAL</option>
              <option value="TWILLIO">TWILLIO</option>
              <option value="FORTICS">FORTICS</option>
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="channel">Canal</FormLabel>
            <FormInput
              id="channel"
              name="channel"
              value={formData.channel}
              onChange={handleInputChange}
              placeholder="all"
            />
          </FormField>

          {showVoiceField && (
            <FormField>
              <FormLabel htmlFor="voice_number">Número de Voz (Twilio)</FormLabel>
              <FormInput
                id="voice_number"
                name="voice_number"
                type="tel"
                value={formData.voice_number}
                onChange={handleInputChange}
                placeholder="+1234567890"
              />
            </FormField>
          )}

          <FormField>
            <FormLabel htmlFor="integration_id">Integração (opcional)</FormLabel>
            <FormSelect
              id="integration_id"
              name="integration_id"
              value={formData.integration_id}
              onChange={handleInputChange}
            >
              <option value="">Selecione uma integração (opcional)</option>
              {integrations?.map((integration: Integration) => (
                <option key={integration.id} value={integration.id}>
                  {integration.description}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="messages_per_minute">Mensagens por Minuto</FormLabel>
              <FormInput
                id="messages_per_minute"
                name="messages_per_minute"
                type="number"
                min="1"
                max="60"
                value={formData.messages_per_minute}
                onChange={handleInputChange}
                placeholder="2"
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="messages_per_hour">Mensagens por Hora</FormLabel>
              <FormInput
                id="messages_per_hour"
                name="messages_per_hour"
                type="number"
                min="1"
                max="1000"
                value={formData.messages_per_hour}
                onChange={handleInputChange}
                placeholder="20"
              />
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="messages_time_condition_id">Condição de Horário de Envio (opcional)</FormLabel>
            <FormSelect
              id="messages_time_condition_id"
              name="messages_time_condition_id"
              value={formData.messages_time_condition_id}
              onChange={handleInputChange}
            >
              <option value="">Sem restrição de horário</option>
              {timeConditions?.map((tc) => (
                <option key={tc.id} value={tc.id}>
                  {tc.description}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="send_alert_at">Enviar Alerta em</FormLabel>
            <FormInput
              id="send_alert_at"
              name="send_alert_at"
              type="datetime-local"
              value={formData.send_alert_at}
              onChange={handleInputChange}
            />
          </FormField>

          <FormField>
            <div className="flex items-center">
              <FormCheckbox
                id="isDefault"
                name="isDefault"
                value="true"
                checked={formData.isDefault}
                onChange={handleInputChange}
              />
              <FormLabel htmlFor="isDefault" className="ml-2 mb-0">
                Definir como sessão padrão
              </FormLabel>
            </div>
          </FormField>
        </div>
      </div>

      {showSmtpFields && (
        <div className={activeTab === 'smtp' ? 'block' : 'hidden'}>
          <div className="space-y-4">
            <FormField>
              <FormLabel htmlFor="smtp_host" required>
                Host SMTP
              </FormLabel>
              <FormInput
                id="smtp_host"
                name="smtp_host"
                value={formData.smtp_host}
                onChange={handleInputChange}
                placeholder="smtp.gmail.com"
                required
              />
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField>
                <FormLabel htmlFor="smtp_port" required>
                  Porta SMTP
                </FormLabel>
                <FormInput
                  id="smtp_port"
                  name="smtp_port"
                  type="number"
                  min="1"
                  max="65535"
                  value={formData.smtp_port}
                  onChange={handleInputChange}
                  placeholder="587"
                  required
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="smtp_username" required>
                  Usuário SMTP
                </FormLabel>
                <FormInput
                  id="smtp_username"
                  name="smtp_username"
                  value={formData.smtp_username}
                  onChange={handleInputChange}
                  placeholder="usuario@exemplo.com"
                  required
                />
              </FormField>
            </div>

            <FormField>
              <FormLabel htmlFor="smtp_password">
                Senha SMTP {session && '(deixe em branco para não alterar)'}
              </FormLabel>
              <FormInput
                id="smtp_password"
                name="smtp_password"
                type="password"
                value={formData.smtp_password}
                onChange={handleInputChange}
                placeholder={session ? '••••••••' : 'Digite a senha'}
                required={!session}
              />
            </FormField>

            <FormField>
              <div className="flex items-center">
                <FormCheckbox
                  id="smtp_secure"
                  name="smtp_secure"
                  value="true"
                  checked={formData.smtp_secure}
                  onChange={handleInputChange}
                />
                <FormLabel htmlFor="smtp_secure" className="ml-2 mb-0">
                  Usar conexão segura (TLS/SSL)
                </FormLabel>
              </div>
            </FormField>
          </div>
        </div>
      )}

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {session ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default SessionForm;
